import React from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import Select from "react-select";
import Button from "components/ui/Button";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import { HiChevronDown } from "react-icons/hi";
import { FiTrash2, FiAlertCircle } from "react-icons/fi";

const capitalize = (str) => str && str.charAt(0).toUpperCase() + str.slice(1);

const ALL_NETWORKS = { value: "", label: "All networks" };

const TemplateEdit = ({
  initialData,
  onSubmit,
  tags,
  showAgentProTipDraft = false,
  // Vendor templates only — buyer templates have no such field on their schema.
  showWhenItGoesOut = false,
  // Pass the networks to offer a network-specific template; omit for templates
  // that are always global.
  networks = null,
}) => {
  // Transform tags for defaultValues and options
  const tagsOptions = tags.map((tag) => ({
    value: tag._id,
    label: tag.name,
  }));

  // Get initial tags selection
  const getInitialTags = () => {
    if (!initialData || !initialData.tags) return [];
    return initialData.tags.map((tag) => ({
      value: tag._id,
      label: tag.name,
    }));
  };

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: initialData?.name || "",
      description: initialData?.description || "",
      purpose: initialData?.purpose || "",
      tags: getInitialTags(),
      sender: initialData?.sender
        ? {
            value: initialData?.sender,
            label: capitalize(initialData?.sender),
          }
        : "",
      specialInstructions: initialData?.specialInstructions || "",
      prompt: initialData?.prompt || "",
      agentProTipDraft: initialData?.agentProTipDraft || "",
      approval: initialData?.approval || false,
      whenItGoesOut: initialData?.whenItGoesOut || "",
      // Populated by the API, so it arrives as { _id, name }.
      networkId: initialData?.networkId
        ? {
            value: initialData.networkId._id,
            label: initialData.networkId.name,
          }
        : ALL_NETWORKS,
      saleProcess: initialData?.saleProcess?.length
        ? initialData?.saleProcess.map((item) => ({
            value: item,
            label: capitalize(item),
          }))
        : [],
      objections: initialData?.objections || [],
    },
  });

  // Setup field array for objections
  const { fields, append, remove } = useFieldArray({
    control,
    name: "objections",
  });

  const objections = watch("objections");

  const submitForm = (data) => {
    const formattedData = {
      name: data?.name,
      description: data?.description,
      purpose: data?.purpose,
      tags: data?.tags?.map((tag) => tag.value),
      specialInstructions: data?.specialInstructions,
      prompt: data?.prompt,
      approval: data?.approval,
      sender: data?.sender?.value,
      saleProcess: data?.saleProcess?.map((item) => item.value),
      objections: data?.objections || [],
    };
    if (showAgentProTipDraft) {
      formattedData.agentProTipDraft = data?.agentProTipDraft || "";
    }
    if (showWhenItGoesOut) {
      formattedData.whenItGoesOut = data?.whenItGoesOut || "";
    }
    if (networks) {
      formattedData.networkId = data?.networkId?.value || null;
    }
    onSubmit(formattedData);
  };

  const saleProcessOptions = [
    { value: "Auction", label: "Auction" },
    { value: "Private Treaty", label: "Private Treaty" },
  ];

  const senderOptions = [
    { value: "admin", label: "Admin" },
    { value: "agent", label: "Agent" },
  ];

  return (
    <form onSubmit={handleSubmit(submitForm)} className="space-y-4 p-2">
      <div className="space-y-2">
        <label htmlFor="name" className="form-label">
          Name
        </label>
        <input
          type="text"
          id="name"
          {...register("name", { required: true })}
          placeholder="Enter name"
          className="form-input border border-mediumgray"
        />
        {errors.name && <p className="form-error-message">Name is required</p>}
      </div>

      <div className="space-y-2">
        <label htmlFor="description" className="form-label">
          Description
        </label>
        <textarea
          id="description"
          {...register("description")}
          placeholder="Enter description"
          rows={3}
          className="form-input border border-mediumgray"
        />
      </div>

      <div className="space-y-2">
        <label htmlFor="purpose" className="form-label">
          Purpose
        </label>
        <textarea
          id="purpose"
          {...register("purpose")}
          placeholder="Enter purpose"
          rows={3}
          className="form-input border border-mediumgray"
        />
      </div>

      {showWhenItGoesOut && (
      <div className="space-y-2">
        <label htmlFor="whenItGoesOut" className="form-label">
          When it goes out
        </label>
        <p className="text-xs text-darkgray">
          Plain words, for someone who has never seen the code. One short line
          per condition, one per row. Say what has to happen, not how it is
          checked. Start each line with a verb and name the real thing an agent
          would recognise — "the marketing is booked in", not "the events
          array". Cover the wait, then anything that stops it:
        </p>
        <pre className="whitespace-pre-wrap rounded bg-lightgray p-2 text-xs text-darkergray">
          {"Goes out 30 minutes after the marketing is booked in.\nSkipped on vacant land.\nStops once the property is sold."}
        </pre>
        <textarea
          id="whenItGoesOut"
          {...register("whenItGoesOut")}
          placeholder={
            "Goes out 30 minutes after the marketing is booked in.\nSkipped on vacant land.\nStops once the property is sold."
          }
          rows={5}
          className="form-input border border-mediumgray"
        />
      </div>
      )}

      <div className="space-y-2">
        <label htmlFor="specialInstructions" className="form-label">
          Special Instructions
        </label>
        <textarea
          id="specialInstructions"
          {...register("specialInstructions")}
          placeholder="Enter special instructions"
          rows={3}
          className="form-input border border-mediumgray"
        />
      </div>

      <div className="space-y-2">
        <label htmlFor="prompt" className="form-label">
          Prompt
        </label>
        <textarea
          id="prompt"
          {...register("prompt")}
          placeholder="Enter prompt"
          rows={3}
          className="form-input border border-mediumgray"
        />
      </div>

      {showAgentProTipDraft && (
        <div className="space-y-2">
          <label htmlFor="agentProTipDraft" className="form-label">
            Agent Pro Tip
          </label>
          <p className="text-xs text-darkgray">
            Sent to the agent word for word — no AI rewrites it. Wrap a part the
            agent must write themselves in braces. Two forms are understood:
            <code className="mx-1 rounded bg-lightgray px-1">
              {"{what you want them to write}"}
            </code>
            for a slot they fill from scratch, and
            <code className="mx-1 rounded bg-lightgray px-1">
              {"{Empathy: (adjust) default wording}"}
            </code>
            for a labelled slot that starts with wording they can adjust.
          </p>
          <textarea
            id="agentProTipDraft"
            {...register("agentProTipDraft")}
            placeholder="Paste the Pro Tip exactly as it should reach the agent"
            rows={8}
            className="form-input border border-mediumgray"
          />
        </div>
      )}

      <div className="space-y-2">
        <label htmlFor="tags" className="form-label">
          Tags
        </label>
        <Controller
          name="tags"
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              options={tagsOptions}
              isMulti
              placeholder="Select tags"
              className="basic-multi-select"
              classNamePrefix="select"
            />
          )}
        />
      </div>

      <div className="space-y-2">
        <label htmlFor="approval" className="form-label">
          Approval
        </label>
        <input
          type="checkbox"
          id="approval"
          {...register("approval")}
          className="mt-1 block"
        />
      </div>

      <div className="space-y-2">
        <label htmlFor="sender" className="form-label">
          Sender
        </label>
        <Controller
          name="sender"
          control={control}
          rules={{ required: "Sender is required" }}
          render={({ field }) => (
            <Select
              {...field}
              options={senderOptions}
              placeholder="Select a sender"
            />
          )}
        />
        {errors.sender && (
          <p className="form-error-message">{errors.sender.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <label htmlFor="saleProcess" className="form-label">
          Sale Process
        </label>
        <Controller
          name="saleProcess"
          control={control}
          rules={{ required: "Sale process is required" }}
          render={({ field }) => (
            <Select
              {...field}
              options={saleProcessOptions}
              isMulti
              placeholder="Select one or more sale processes"
            />
          )}
        />
        {errors.saleProcess && (
          <p className="form-error-message">{errors.saleProcess.message}</p>
        )}
      </div>

      {networks && (
        <div className="space-y-2">
          <label htmlFor="networkId" className="form-label">
            Network
          </label>
          <p className="text-xs text-darkgray">
            Pick a network to give only its agents this message. Narrowing an
            existing template takes it back off every other network's
            properties — a drafted message goes with it, one already sent to
            the vendors stays in their log.
          </p>
          <Controller
            name="networkId"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                options={[
                  ALL_NETWORKS,
                  ...networks.map((network) => ({
                    value: network._id,
                    label: network.name,
                  })),
                ]}
                placeholder="All networks"
              />
            )}
          />
        </div>
      )}

      {/* Objections Section */}
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <label className="form-label">
            Objections {fields.length > 0 && `(${fields.length})`}
          </label>
          <Button
            type="button"
            onClick={() =>
              append({ objection: "", preempt: "", howItLands: "" })
            }
            className="text-sm"
          >
            + Add Objection
          </Button>
        </div>

        <div className="space-y-2">
          {fields.map((field, index) => {
            const objectionData = objections?.[index];
            const hasContent = objectionData?.objection?.trim();
            const hasError =
              errors.objections?.[index]?.objection ||
              errors.objections?.[index]?.preempt ||
              errors.objections?.[index]?.howItLands;

            return (
              <Disclosure
                key={field.id}
                as="div"
                className="w-full"
                defaultOpen={false}
              >
                <DisclosureButton
                  className={`p-3 group flex w-full items-center justify-between rounded-md ${
                    hasError ? "bg-red-50" : "bg-lightgray"
                  }`}
                >
                  <span className="font-medium truncate flex-1 text-left flex items-center gap-2">
                    {hasContent ? (
                      <>
                        {index + 1}: {objectionData.objection}
                      </>
                    ) : (
                      <span className="text-darkgray">
                        {index + 1}: Empty objection
                      </span>
                    )}
                    {hasError && <FiAlertCircle className="text-red-500" />}
                  </span>
                  <div className="flex items-center gap-3">
                    <FiTrash2
                      className="cursor-pointer text-red-600 hover:text-red-800"
                      onClick={(e) => {
                        e.stopPropagation();
                        remove(index);
                      }}
                    />
                    <HiChevronDown className="group-data-[open]:rotate-180" />
                  </div>
                </DisclosureButton>
                <DisclosurePanel className="mt-2 p-4 bg-white border border-mediumgray rounded space-y-3">
                  <div className="space-y-2">
                    <label className="form-label text-sm">Objection</label>
                    <input
                      type="text"
                      {...register(`objections.${index}.objection`, {
                        required: "Objection is required",
                      })}
                      placeholder="Enter the objection"
                      className={`form-input w-full border ${
                        errors.objections?.[index]?.objection
                          ? "border-red-500"
                          : "border-mediumgray"
                      }`}
                    />
                    {errors.objections?.[index]?.objection && (
                      <p className="form-error-message text-xs">
                        {errors.objections[index].objection.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="form-label text-sm">Preempt</label>
                    <textarea
                      {...register(`objections.${index}.preempt`, {
                        required: "Preempt is required",
                      })}
                      placeholder="How to preempt this objection"
                      rows={2}
                      className={`form-input w-full border ${
                        errors.objections?.[index]?.preempt
                          ? "border-red-500"
                          : "border-mediumgray"
                      }`}
                    />
                    {errors.objections?.[index]?.preempt && (
                      <p className="form-error-message text-xs">
                        {errors.objections[index].preempt.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="form-label text-sm">How It Lands</label>
                    <textarea
                      {...register(`objections.${index}.howItLands`, {
                        required: "How it lands is required",
                      })}
                      placeholder="Describe how this lands with the customer"
                      rows={2}
                      className={`form-input w-full border ${
                        errors.objections?.[index]?.howItLands
                          ? "border-red-500"
                          : "border-mediumgray"
                      }`}
                    />
                    {errors.objections?.[index]?.howItLands && (
                      <p className="form-error-message text-xs">
                        {errors.objections[index].howItLands.message}
                      </p>
                    )}
                  </div>
                </DisclosurePanel>
              </Disclosure>
            );
          })}

          {fields.length === 0 && (
            <p className="text-darkgray text-sm italic">
              No objections added yet. Click "Add Objection" to get started.
            </p>
          )}
        </div>
      </div>

      <Button type="submit" className="black-button">
        Save
      </Button>
    </form>
  );
};

export default TemplateEdit;
